import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'components/Button';
import { PhoneStatusPill } from 'components/PhoneStatus';
import { getUserDetail, rerunPhoneLookup } from 'data/api/user.api';
import { UserInfo } from '@data/models/user.model';
import { PhoneLookupFields, phonePill } from 'utils/phonePill';
import { savePhoneLookup, selectPhoneLookup } from 'store/userSlide';

const labelSx = { fontSize: '13px', color: '#7D818C', lineHeight: '20px' };
const valueSx = {
  fontSize: '14px',
  fontWeight: 700,
  color: '#213B54',
  lineHeight: '20px',
  wordBreak: 'break-word',
};

interface Props {
  user: UserInfo;
}

const lookupFieldsOf = (user: UserInfo): PhoneLookupFields => ({
  phone: user.phone,
  phoneType: user.phoneType,
  phoneLineStatus: user.phoneLineStatus,
  phoneLookupStatus: user.phoneLookupStatus,
  phoneLookupCheckedAt: user.phoneLookupCheckedAt,
});

/**
 * BL-EPIC-239. Renders nothing for a number that has never been checked, which
 * is every member until the lookup is switched on.
 */
const PhoneVerification = ({ user }: Props) => {
  const dispatch = useDispatch();
  const [running, setRunning] = React.useState(false);
  // A re-run's result outlives this component (switching record tabs remounts
  // it) until the user is next loaded. The lookup always runs on the SAVED
  // phone, which is what these show.
  const rerunResult = useSelector(selectPhoneLookup);
  const lookup =
    rerunResult &&
    rerunResult.userId === user.userId &&
    // A fresher load of a changed number outranks a late re-run result.
    rerunResult.fields.phone === user.phone
      ? rerunResult.fields
      : lookupFieldsOf(user);

  if (!user.userId || !phonePill(lookup)) return null;

  const rerun = async () => {
    setRunning(true);
    try {
      await rerunPhoneLookup(user.userId as string);
      toast.success('Phone verification re-run.');
      const { user: saved } = await getUserDetail(user.userId as string);
      dispatch(
        savePhoneLookup({
          userId: user.userId as string,
          fields: lookupFieldsOf(saved),
        }),
      );
    } catch (error) {
      toast.error(
        typeof error === 'string'
          ? error
          : 'Phone verification could not be re-run.',
      );
    } finally {
      setRunning(false);
    }
  };

  const fields = [
    { label: 'Phone Type', value: lookup.phoneType },
    { label: 'Line Status', value: lookup.phoneLineStatus },
    { label: 'Lookup Status', value: lookup.phoneLookupStatus },
    {
      label: 'Checked At',
      value: lookup.phoneLookupCheckedAt
        ? moment
            .utc(lookup.phoneLookupCheckedAt)
            .local()
            .format('MM/DD/YYYY hh:mm A')
        : null,
    },
  ];

  return (
    <Box
      sx={{
        border: '1px dashed #DBD8D8',
        borderRadius: '8px',
        padding: '16px 20px',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <ShieldOutlinedIcon sx={{ fontSize: '18px', color: '#213B54' }} />
          <Typography
            component="h3"
            sx={{ fontSize: '15px', fontWeight: 700, color: '#213B54' }}
          >
            Phone Verification
          </Typography>
          <PhoneStatusPill member={lookup} />
        </Stack>
        <Button
          variant="outlined"
          size="small"
          disabled={running}
          startIcon={<AutorenewIcon />}
          onClick={rerun}
          sx={{ fontWeight: 700, fontSize: '14px', paddingX: '16px' }}
        >
          {running ? 'Re-running…' : 'Re-run Verification'}
        </Button>
      </Stack>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2,
          margin: '16px 0 0',
        }}
      >
        {fields.map(({ label, value }) => (
          <Box key={label}>
            <Typography component="dt" sx={labelSx}>
              {label}
            </Typography>
            <Typography component="dd" sx={{ ...valueSx, margin: 0 }}>
              {value || '-'}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PhoneVerification;
